import {createContext, useCallback, use, useEffect, useMemo, useRef, type ReactNode, useState} from 'react'
import type {ConcreteRequest, Environment} from 'relay-runtime'
import {DEFAULT_RELAY_CACHE_TTL} from '../helpers'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useRelayEnvironment} from 'react-relay'
import {fetchQueryWithCache} from '../query-helpers'

type CachePreheatContextType = {
  mode: 'relay' | 'tanstack'
  markQueryForPreheating: (
    query: ConcreteRequest,
    variables: Record<string, unknown>,
    source: string,
    excludeFromCacheKey?: string[],
    highPriority?: boolean,
  ) => void
  markTaskForPreheating: (key: string, task: () => Promise<unknown>, highPriority?: boolean) => void
  preHeatQueries: () => Promise<void>
  resetQueue: () => void
}

type CachePreheatContextProviderProps = {
  children: ReactNode
  enabled?: boolean
}

// This is just a safeguard against extreme cases (like an issue that has a thousands of issues links in comments)
export const MAX_QUERIES_TO_MARK_FOR_PREHEATING = 50

const PREHEAT_REQUESTS_BATCH_SIZE = 4

const CachePreheatContext = createContext<CachePreheatContextType | null>(null)

type QueriesToPreheat = Record<
  string,
  {query: ConcreteRequest; variables: Record<string, unknown>; source: string; excludeFromCacheKey?: string[]}
>

type TasksToPreheat = Record<string, () => Promise<unknown>>

const taskKey = (key: string) => `task:${key}`

export function TanStackCachePreheatContextProvider({children, enabled = true}: CachePreheatContextProviderProps) {
  return (
    <CachePreheatProvider enabled={enabled} mode="tanstack">
      {children}
    </CachePreheatProvider>
  )
}

export function RelayCachePreheatContextProvider({children, enabled = true}: CachePreheatContextProviderProps) {
  const relayEnvironment = useRelayEnvironment()
  return (
    <CachePreheatProvider enabled={enabled} mode="relay" relayEnvironment={relayEnvironment}>
      {children}
    </CachePreheatProvider>
  )
}

function CachePreheatProvider({
  children,
  enabled,
  mode,
  relayEnvironment,
}: Required<CachePreheatContextProviderProps> & {
  mode: CachePreheatContextType['mode']
  relayEnvironment?: Environment
}) {
  const queriesToPreheatRef = useRef<QueriesToPreheat>({})
  const queriesToPreheatInHotCacheRef = useRef<QueriesToPreheat>({})
  const tasksToPreheatRef = useRef<TasksToPreheat>({})
  const keysRef = useRef<Set<string>>(new Set())
  const prioritizedKeysRef = useRef<Set<string>>(new Set())
  const preheatedRef = useRef<Set<string>>(new Set())
  const keysForHotCacheRef = useRef<Set<string>>(new Set())
  const preheatedHotCacheRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState(false)
  const [pendingHotCache, setPendingHotCache] = useState(false)

  const preheatQuery = useCallback(
    async (key: string, forHotCache: boolean): Promise<boolean> => {
      if (!enabled) return false

      const hasKey = forHotCache ? preheatedHotCacheRef.current.has(key) : preheatedRef.current.has(key)
      if (!hasKey) {
        const taskToPreheat = forHotCache ? undefined : tasksToPreheatRef.current[key]
        if (taskToPreheat) {
          preheatedRef.current.add(key)
          try {
            await taskToPreheat()
          } catch {
            // Silently handle errors - preheat failures shouldn't break the app
          }
          return true
        }
        const queryToPreheat = forHotCache
          ? queriesToPreheatInHotCacheRef.current[key]
          : queriesToPreheatRef.current[key]
        if (queryToPreheat && mode === 'relay' && relayEnvironment) {
          if (!forHotCache) {
            preheatedRef.current.add(key)
          }
          preheatedHotCacheRef.current.add(key)
          try {
            await fetchQueryWithCache(
              relayEnvironment,
              queryToPreheat.query,
              queryToPreheat.variables,
              {fetchPolicy: 'store-or-network'},
              DEFAULT_RELAY_CACHE_TTL,
              queryToPreheat.source,
              false,
              queryToPreheat.excludeFromCacheKey,
              forHotCache,
            ).toPromise()
          } catch {
            // Silently handle errors - preheat failures shouldn't break the app
          }
          return true
        }
      }
      return false
    },
    [enabled, mode, relayEnvironment],
  )

  const markQueryForPreheating = useCallback(
    (
      query: ConcreteRequest,
      variables: Record<string, unknown>,
      source: string,
      excludeFromCacheKey?: string[],
      highPriority?: boolean,
    ) => {
      if (!enabled || mode !== 'relay') return

      const queryName = query.fragment.name
      // generate a key from the query name and sorted variables to avoid duplicates
      const filteredVariables = {...variables}
      if (excludeFromCacheKey && excludeFromCacheKey.length > 0) {
        for (const key of excludeFromCacheKey) {
          delete filteredVariables[key]
        }
      }
      const key = `${queryName}#${JSON.stringify(
        Object.fromEntries(Object.entries(filteredVariables).sort(([a], [b]) => (a < b ? -1 : 1))),
      )}`
      if (!keysForHotCacheRef.current.has(key)) {
        queriesToPreheatInHotCacheRef.current[key] = {query, variables, source, excludeFromCacheKey}
        keysForHotCacheRef.current.add(key)
        setPendingHotCache(true)
      }
      if (highPriority) {
        if (!prioritizedKeysRef.current.has(key)) {
          queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
          prioritizedKeysRef.current.add(key)
          setPending(true)
        }
      } else if (!keysRef.current.has(key)) {
        queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
        keysRef.current.add(key)
        setPending(true)
      }
    },
    [enabled, mode],
  )

  const markTaskForPreheating = useCallback(
    (key: string, task: () => Promise<unknown>, highPriority?: boolean) => {
      // Matches `markQueryForPreheating`: a disabled provider must not retain work or mark itself
      // pending, or tasks registered while disabled would run if it were ever enabled.
      if (!enabled) return

      const keyWithType = taskKey(key)
      if (preheatedRef.current.has(keyWithType)) {
        return
      }
      if (highPriority && keysRef.current.delete(keyWithType)) {
        prioritizedKeysRef.current.add(keyWithType)
        return
      }
      if (prioritizedKeysRef.current.has(keyWithType) || keysRef.current.has(keyWithType)) {
        return
      }
      const targetKeys = highPriority ? prioritizedKeysRef.current : keysRef.current

      tasksToPreheatRef.current[keyWithType] = task
      targetKeys.add(keyWithType)
      setPending(true)
    },
    [enabled],
  )

  const preheatQuerySet = useCallback(
    (promises: Array<Promise<boolean>>, querySet: Set<string>) => {
      for (const key of querySet) {
        if (promises.length >= PREHEAT_REQUESTS_BATCH_SIZE) {
          break
        }
        if (!preheatedRef.current.has(key)) {
          promises.push(preheatQuery(key, false))
        }
      }
      return promises
    },
    [preheatQuery],
  )

  const preHeatQueries = useCallback(async () => {
    const promises = preheatQuerySet([], prioritizedKeysRef.current)
    preheatQuerySet(promises, keysRef.current)
    await Promise.all(promises)
    const queuedKeys = new Set([...prioritizedKeysRef.current, ...keysRef.current])
    if (preheatedRef.current.size >= queuedKeys.size) {
      // All queries have been preheated
      setPending(false)
    }
  }, [preheatQuerySet])

  useEffect(() => {
    if (!enabled || !pending) return

    let cancelled = false

    const scheduleNextPreheat = () => {
      if (cancelled || !('requestIdleCallback' in window)) return

      requestIdleCallback(() => {
        if (cancelled || !pending) return
        const runPreheat = async () => {
          await preHeatQueries()
          scheduleNextPreheat()
        }
        void runPreheat()
      })
    }

    scheduleNextPreheat()

    return () => {
      cancelled = true
    }
  }, [enabled, preHeatQueries, pending])

  useEffect(() => {
    if (!enabled || mode !== 'relay' || !pendingHotCache) return

    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        const keys = [...keysForHotCacheRef.current].filter(key => !preheatedHotCacheRef.current.has(key))
        if (keys.length === 0) {
          setPendingHotCache(false)
          return
        }

        await Promise.all(keys.map(key => preheatQuery(key, true)))
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [enabled, mode, pendingHotCache, preheatQuery])

  const resetQueue = useCallback(() => {
    queriesToPreheatRef.current = {}
    queriesToPreheatInHotCacheRef.current = {}
    tasksToPreheatRef.current = {}
    keysRef.current = new Set()
    prioritizedKeysRef.current = new Set()
    preheatedRef.current = new Set()
    keysForHotCacheRef.current = new Set()
    preheatedHotCacheRef.current = new Set()
    setPending(false)
    setPendingHotCache(false)
  }, [])

  const contextValue: CachePreheatContextType = useMemo(() => {
    return {
      mode,
      markQueryForPreheating,
      markTaskForPreheating,
      preHeatQueries,
      resetQueue,
    }
  }, [mode, markQueryForPreheating, markTaskForPreheating, preHeatQueries, resetQueue])

  return <CachePreheatContext value={contextValue}>{children}</CachePreheatContext>
}

export const useRelayCachePreheatMode = () => use(CachePreheatContext)?.mode ?? 'relay'

export const useRelayCachePreheatContext = () => {
  const context = use(CachePreheatContext)
  if (!context) {
    // Fail silently to avoid issues in tests or if used outside of provider
    // eslint-disable-next-line no-console
    console.warn('useRelayCachePreheatContext must be used within a RelayCachePreheatContextProvider')
    return {
      mode: 'relay' as const,
      markQueryForPreheating: () => {},
      markTaskForPreheating: () => {},
      preHeatQueries: () => Promise.resolve(),
      resetQueue: () => {},
    }
  }

  return context
}

export const useCachePreheatContext = () => {
  const context = use(CachePreheatContext)
  if (!context) {
    // Fail silently to avoid issues in tests or if used outside of provider
    // eslint-disable-next-line no-console
    console.warn('useCachePreheatContext must be used within a TanStackCachePreheatContextProvider')
    return {
      mode: 'tanstack' as const,
      markQueryForPreheating: () => {},
      markTaskForPreheating: () => {},
      preHeatQueries: () => Promise.resolve(),
      resetQueue: () => {},
    }
  }

  return context
}
